/**
 * PATH: apps/qa/src/pages/new-inspection/Toast.tsx
 * PURPOSE: Lightweight toast notification for contextual hints
 */

import { useEffect } from 'react'
import { cn } from '@/lib/utils'
import type { ToastNotification } from './inspection-types'

export function Toast({
  notification,
  onDismiss,
}: {
  notification: ToastNotification
  onDismiss: (id: string) => void
}) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(notification.id), 5000)
    return () => clearTimeout(timer)
  }, [notification.id, onDismiss])

  const bgColors = {
    info: 'bg-primary/10 border-primary/30 text-primary',
    success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300',
    warning: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300',
    error: 'bg-destructive/10 border-destructive/30 text-destructive',
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg animate-in slide-in-from-top-2 fade-in duration-200',
        bgColors[notification.type]
      )}
      role="alert"
    >
      <span className="text-sm flex-1">{notification.message}</span>
      {notification.action && (
        <button
          onClick={notification.action.onClick}
          className="text-sm font-medium underline underline-offset-2 hover:no-underline transition-all"
        >
          {notification.action.label}
        </button>
      )}
      <button
        onClick={() => onDismiss(notification.id)}
        className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
        aria-label="Dismiss notification"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}
